import { createFileRoute, redirect } from "@tanstack/react-router";
import type { RouterContextWithSession } from "../../../router";
import { useState, useEffect } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useProfile, useUpdateProfile } from "../../../hooks/useProfile";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { toast } from "../../../components/ui/sonner";
import { ThemeSettings } from "../../../components/settings/ThemeSettings";
import { NotificationPreferences } from "../../../components/settings/NotificationPreferences";
import { AISettings } from "../../../components/settings/AISettings";
import { McpSettingsPanel } from "../../../components/settings/McpSettingsPanel";
import { User, Palette, Bell, Bot, Plug } from "lucide-react";

export const Route = createFileRoute("/_admin/settings/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Settings"),
  component: SettingsPage,
});

function SettingsPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <main className="container mx-auto p-6 space-y-6 max-w-5xl">
      <Card className="bg-card/60 backdrop-blur-md border-border/40 p-6 shadow-xl space-y-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Personal Settings</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage your profile, appearance, notifications, AI preferences, and MCP
            connections.
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 md:w-fit md:inline-flex">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="theme" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              <span className="hidden sm:inline">Theme</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              <span className="hidden sm:inline">AI</span>
            </TabsTrigger>
            <TabsTrigger value="mcp" className="flex items-center gap-2">
              <Plug className="w-4 h-4" />
              <span className="hidden sm:inline">MCP</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6 pt-2">
            <ProfileSettings userId={user?.id ?? ""} accessToken={session?.access_token ?? ""} />
          </TabsContent>

          <TabsContent value="theme" className="space-y-6 pt-2">
            <ThemeSettings />
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6 pt-2">
            <NotificationPreferences />
          </TabsContent>

          <TabsContent value="ai" className="space-y-6 pt-2">
            <AISettings />
          </TabsContent>

          <TabsContent value="mcp" className="space-y-6 pt-2">
            <McpSettingsPanel />
          </TabsContent>
        </Tabs>
      </Card>
    </main>
  );
}

function ProfileSettings({ userId, accessToken }: { userId: string; accessToken: string }) {
  const { data: profile, isLoading, error } = useProfile(userId, accessToken);
  const updateProfile = useUpdateProfile();

  const [fullName, setFullName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? "");
      setAvatarUrl(profile.avatar_url ?? "");
    }
  }, [profile]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !accessToken) return;

    const updates: { full_name?: string; avatar_url?: string } = {};
    if (fullName.trim()) updates.full_name = fullName.trim();
    if (avatarUrl.trim()) updates.avatar_url = avatarUrl.trim();

    updateProfile.mutate(
      { userId, accessToken, updates },
      {
        onSuccess: () => toast.success("Profile updated successfully."),
        onError: (err) => toast.error(err?.message ?? "Failed to update profile."),
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading profile…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error.message}</p>
        ) : (
          <form onSubmit={handleSave} className="space-y-4 max-w-md">
            <div className="space-y-1">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="avatar_url">Avatar URL</Label>
              <Input
                id="avatar_url"
                type="url"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://example.com/avatar.png"
              />
            </div>
            <Button type="submit" loading={updateProfile.isPending} disabled={updateProfile.isPending}>
              Save Changes
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
